/**
 * The one request helper for client components: JSON in, JSON out, and a
 * single error contract. Every API route refuses with `{ error }` (see
 * `jsonError`), so a refused response rejects with a RequestError carrying
 * that message, or the caller's fallback when the body has none.
 */

/** Untyped by default, exactly like `response.json()`. */
type Json = Awaited<ReturnType<Response["json"]>>;

/** A response the server refused (`!response.ok`), with what it sent back. */
export class RequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly data: unknown,
  ) {
    super(message);
    this.name = "RequestError";
  }
}

export type RequestOptions = Omit<RequestInit, "body"> & {
  /** A plain object or array is sent as JSON; FormData, strings and other bodies go through untouched. */
  body?: BodyInit | object | null;
  /** The message when the server refused without an `error` of its own. */
  fallback?: string | ((response: Response) => string);
};

function isJsonBody(body: RequestOptions["body"]): body is object {
  if (typeof body !== "object" || body === null) return false;
  if (Array.isArray(body)) return true;
  const proto = Object.getPrototypeOf(body);
  return proto === Object.prototype || proto === null;
}

/**
 * Fetches `url`, parses the body as JSON (null when it is not JSON) and
 * returns it. An aborted signal rejects even after the response arrived,
 * so a cancelled scope never sees its data.
 */
export async function requestJson<T = Json>(
  url: string,
  { body, fallback, ...init }: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const json = isJsonBody(body);
  if (json && !headers.has("content-type")) headers.set("content-type", "application/json");
  const response = await fetch(url, {
    ...init,
    headers,
    body: json ? JSON.stringify(body) : (body as BodyInit | null | undefined),
  });
  const data: unknown = await response.json().catch(() => null);
  init.signal?.throwIfAborted();
  if (!response.ok) {
    const error = (data as { error?: unknown } | null)?.error;
    throw new RequestError(
      typeof error === "string" && error
        ? error
        : typeof fallback === "function"
          ? fallback(response)
          : (fallback ?? `Request failed (${response.status}).`),
      response.status,
      data,
    );
  }
  return data as T;
}

/**
 * For a request whose answer the caller never read: a refusal resolves to
 * null as it was ignored before, while a failed fetch still rejects.
 */
export function ignoreRefusal(cause: unknown): null {
  if (cause instanceof RequestError) return null;
  throw cause;
}
